import { useState, useRef, useCallback } from 'react';
import { clientsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Search, UserPlus, User, Camera, ArrowLeft, ArrowRight, Check, Phone, Loader2 } from 'lucide-react';

export default function StepClient({ wizardState, updateState, nextStep, prevStep }) {
  const [mode, setMode] = useState(null); // null | 'existing' | 'new'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState({ first_name: '', last_name: '', phone: '' });
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const searchClients = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await clientsAPI.search(q);
      setSearchResults(res.data || []);
    } catch { setSearchResults([]); }
    setSearching(false);
  }, []);

  const selectExistingClient = async (client) => {
    // Find a suitable photo
    let photo = null;
    try {
      const photosRes = await clientsAPI.listPhotos(client.id);
      const photos = photosRes.data || [];
      photo = photos.find(p => p.tryon_suitable) || photos.find(p => p.photo_type === 'profile') || photos[0];
    } catch {}

    updateState({
      client: client,
      clientPhoto: photo?.url || photo?.photo_url || null,
    });
    toast.success(`Selected: ${client.first_name} ${client.last_name}`);
  };

  const startCamera = async () => {
    setCameraActive(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      toast.error('Camera access denied');
      setCameraActive(false);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    ctx.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedPhoto(dataUrl);
    stopCamera();
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const createClient = async () => {
    if (!newClient.first_name.trim()) { toast.error('First name is required'); return; }
    if (!newClient.phone.trim()) { toast.error('Phone number is required'); return; }

    setCreating(true);
    try {
      const res = await clientsAPI.create({
        first_name: newClient.first_name.trim(),
        last_name: newClient.last_name.trim(),
        phone: newClient.phone.trim(),
        source: 'walk_in',
      });
      const created = res.data;

      // Upload photo if captured
      let photoUrl = null;
      if (capturedPhoto) {
        const blob = await (await fetch(capturedPhoto)).blob();
        const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
        const photoRes = await clientsAPI.uploadPhoto(created.id, file, 'profile', 'Client mode photo');
        photoUrl = photoRes.data?.url || photoRes.data?.photo_url;
      }

      updateState({
        client: created,
        clientPhoto: photoUrl || capturedPhoto,
      });
      toast.success('Client created!');
    } catch (err) {
      toast.error('Failed to create client');
    }
    setCreating(false);
  };

  const handleProceed = () => {
    if (!wizardState.client) {
      toast.error('Please select or create a client first');
      return;
    }
    nextStep();
  };

  // Choose mode screen
  if (!mode) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="step-client">
        <h1 className="text-3xl lg:text-4xl font-light tracking-tight" style={{ color: 'var(--brand-navy)', fontFamily: "'Playfair Display', serif" }}>
          Who Is This For?
        </h1>
        <p className="mt-3 text-base" style={{ color: 'var(--ink-muted)' }}>
          Let us personalise your experience
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
          <button
            onClick={() => setMode('existing')}
            className="group p-8 rounded-2xl border-2 transition-all duration-300 hover:shadow-lg text-left"
            style={{ borderColor: 'rgba(27,42,74,0.08)', backgroundColor: 'white' }}
            data-testid="existing-client-btn"
          >
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
              <User className="w-7 h-7" style={{ color: 'var(--brand-navy)' }} />
            </div>
            <p className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>Returning Client</p>
            <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>Search by name or phone number</p>
          </button>

          <button
            onClick={() => { setMode('new'); setTimeout(startCamera, 500); }}
            className="group p-8 rounded-2xl border-2 transition-all duration-300 hover:shadow-lg text-left"
            style={{ borderColor: 'rgba(197,165,90,0.3)', backgroundColor: 'rgba(197,165,90,0.03)' }}
            data-testid="new-client-btn"
          >
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(197,165,90,0.1)' }}>
              <UserPlus className="w-7 h-7" style={{ color: 'var(--brand-gold)' }} />
            </div>
            <p className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>New Client</p>
            <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>Quick setup with a photo</p>
          </button>
        </div>

        <Button variant="ghost" onClick={prevStep} className="mt-10 gap-2 text-sm" style={{ color: 'var(--ink-muted)' }}>
          <ArrowLeft className="w-4 h-4" /> Back to Fabrics
        </Button>
      </div>
    );
  }

  // Existing client search
  if (mode === 'existing') {
    return (
      <div className="max-w-xl mx-auto px-6 py-12" data-testid="step-client-existing">
        <button onClick={() => setMode(null)} className="flex items-center gap-1 text-xs mb-6" style={{ color: 'var(--ink-muted)' }}>
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <h2 className="text-2xl font-light mb-6" style={{ color: 'var(--brand-navy)', fontFamily: "'Playfair Display', serif" }}>
          Find Your Profile
        </h2>

        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--ink-muted)' }} />
          <Input
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); searchClients(e.target.value); }}
            placeholder="Name or phone number..."
            className="h-14 pl-12 rounded-2xl text-base"
            autoFocus
            data-testid="client-search-input"
          />
        </div>

        {searching && <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: 'var(--brand-gold)' }} /></div>}

        <div className="space-y-3">
          {searchResults.map(client => (
            <button
              key={client.id}
              onClick={() => selectExistingClient(client)}
              className="w-full flex items-center gap-4 p-4 rounded-xl border transition-all hover:shadow-md text-left"
              style={{
                borderColor: wizardState.client?.id === client.id ? 'var(--brand-gold)' : 'rgba(27,42,74,0.08)',
                backgroundColor: wizardState.client?.id === client.id ? 'rgba(197,165,90,0.05)' : 'white',
              }}
              data-testid={`client-result-${client.id}`}
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                <span className="text-sm font-bold" style={{ color: 'var(--brand-navy)' }}>
                  {(client.first_name?.[0] || '') + (client.last_name?.[0] || '')}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-base font-semibold" style={{ color: 'var(--brand-navy)' }}>
                  {client.first_name} {client.last_name}
                </p>
                <p className="text-xs flex items-center gap-1" style={{ color: 'var(--ink-muted)' }}>
                  <Phone className="w-3 h-3" /> {client.phone || 'No phone'}
                </p>
              </div>
              {wizardState.client?.id === client.id && (
                <Check className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
              )}
            </button>
          ))}
        </div>

        {/* Selected client + proceed */}
        {wizardState.client && (
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 p-4 rounded-xl border mb-4" style={{ borderColor: 'var(--brand-gold)', backgroundColor: 'rgba(197,165,90,0.05)' }}>
              {wizardState.clientPhoto ? (
                <img src={wizardState.clientPhoto} alt="" className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                  <User className="w-6 h-6" style={{ color: 'var(--brand-navy)' }} />
                </div>
              )}
              <div className="text-left">
                <p className="text-base font-semibold" style={{ color: 'var(--brand-navy)' }}>
                  {wizardState.client.first_name} {wizardState.client.last_name}
                </p>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Selected</p>
              </div>
            </div>
            <div>
              <Button onClick={handleProceed} className="h-12 px-10 rounded-full text-sm font-semibold gap-2 shadow-lg"
                style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="proceed-to-tryon-btn">
                Continue <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // New client form
  return (
    <div className="max-w-xl mx-auto px-6 py-12" data-testid="step-client-new">
      <button onClick={() => { stopCamera(); setMode(null); }} className="flex items-center gap-1 text-xs mb-6" style={{ color: 'var(--ink-muted)' }}>
        <ArrowLeft className="w-3 h-3" /> Back
      </button>
      <h2 className="text-2xl font-light mb-8" style={{ color: 'var(--brand-navy)', fontFamily: "'Playfair Display', serif" }}>
        Welcome! Let's Get Started
      </h2>

      {/* Camera / Photo */}
      <div className="mb-8">
        <Label className="text-sm font-medium mb-2 block" style={{ color: 'var(--brand-navy)' }}>Your Photo</Label>
        <p className="text-xs mb-3" style={{ color: 'var(--ink-muted)' }}>We'll use this to show how fabrics look on you</p>

        {cameraActive ? (
          <div className="relative rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '4/3' }}>
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
              <Button onClick={capturePhoto} className="h-14 w-14 rounded-full shadow-xl" style={{ backgroundColor: 'var(--brand-gold)' }}
                data-testid="capture-photo-btn">
                <Camera className="w-6 h-6" style={{ color: 'var(--brand-navy)' }} />
              </Button>
              <Button onClick={stopCamera} variant="outline" className="h-14 px-6 rounded-full bg-white/90">Cancel</Button>
            </div>
          </div>
        ) : capturedPhoto ? (
          <div className="relative rounded-2xl overflow-hidden" style={{ maxWidth: 300 }}>
            <img src={capturedPhoto} alt="Client" className="w-full rounded-2xl" />
            <Button
              onClick={startCamera}
              variant="outline"
              className="absolute bottom-3 right-3 rounded-full h-9 text-xs gap-1 bg-white/90"
            >
              <Camera className="w-3 h-3" /> Retake
            </Button>
          </div>
        ) : (
          <button onClick={startCamera} className="w-full p-8 rounded-2xl border-2 border-dashed text-center transition-all hover:shadow-md"
            style={{ borderColor: 'rgba(197,165,90,0.4)' }} data-testid="open-camera-btn">
            <Camera className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--brand-gold)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>Tap to Take a Photo</p>
            <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>A front-facing photo works best for try-on</p>
          </button>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Form fields */}
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>First Name *</Label>
            <Input value={newClient.first_name} onChange={e => setNewClient(p => ({ ...p, first_name: e.target.value }))}
              className="h-12 rounded-xl text-base mt-1" placeholder="First name" autoFocus data-testid="new-client-fname" />
          </div>
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Last Name</Label>
            <Input value={newClient.last_name} onChange={e => setNewClient(p => ({ ...p, last_name: e.target.value }))}
              className="h-12 rounded-xl text-base mt-1" placeholder="Last name" data-testid="new-client-lname" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Phone Number *</Label>
          <Input value={newClient.phone} onChange={e => setNewClient(p => ({ ...p, phone: e.target.value }))}
            className="h-12 rounded-xl text-base mt-1" placeholder="+91 98765 43210" type="tel" inputMode="tel" data-testid="new-client-phone" />
        </div>
      </div>

      {/* Already created? Show proceed */}
      {wizardState.client ? (
        <div className="mt-8 text-center">
          <p className="text-sm mb-3" style={{ color: '#059669' }}>
            <Check className="w-4 h-4 inline mr-1" /> Client created successfully
          </p>
          <Button onClick={handleProceed} className="h-12 px-10 rounded-full text-sm font-semibold gap-2 shadow-lg"
            style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="proceed-to-tryon-btn">
            Continue to Try-On <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <Button onClick={createClient} disabled={creating} className="w-full h-12 rounded-xl text-sm font-semibold mt-8 gap-2"
          style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="create-client-btn">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          {creating ? 'Creating...' : 'Create & Continue'}
        </Button>
      )}
    </div>
  );
}
